import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ViewList as ViewListIcon,
  Category as CategoryIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotify } from '../hooks';
import ProductFilters, { buildFilterParams } from '../components/ui/ProductFilters';
import { MP_MAP, MP_COLORS, MP_TEXT_COLORS, pickProductImage } from '../utils/productImage';
import ProductsTabs from '../components/products/ProductsTabs';
import { filterCategoryOptions, isNewCategoryOption } from './productCategoryOptions.mjs';

// Редактируемые поля единого инструмента обновления товаров.
// type: text | money | int. Порядок = порядок колонок в таблице.
const EDITABLE_FIELDS = [
  { field: 'gruppa', label: 'Группа', type: 'text', width: 150, align: 'left' },
  { field: 'podgruppa', label: 'Подгруппа', type: 'text', width: 150, align: 'left' },
  { field: 'cost', label: 'Себестоимость', type: 'money', width: 140, align: 'right' },
  { field: 'outgoing_nds', label: 'Исходящий НДС', type: 'percent', width: 140, align: 'right' },
  { field: 'kratnost', label: 'Кратность', type: 'int', width: 110, align: 'right' },
  { field: 'units_per_pallet', label: 'Шт/палета', type: 'int', width: 120, align: 'right' },
];
const EDITABLE_MAP = Object.fromEntries(EDITABLE_FIELDS.map((f) => [f.field, f]));

export default function Products() {
  const { error: showError, success: showSuccess, warning: showWarning } = useNotify();
  const goToDetails = useCallback((row, isGroupMode) => {
    if (!row?.podgruppa) return;
    const params = new URLSearchParams();
    params.set('podgruppa', row.podgruppa);
    if (row.gruppa) params.set('gruppa', row.gruppa);
    if (!isGroupMode && row.offer_id) {
      params.set('article', row.offer_id);
      if (row.organization) params.set('organization', row.organization);
    }
    window.open(`/analytics/products/details?${params.toString()}`, '_blank', 'noopener,noreferrer');
  }, []);

  const [filters, setFilters] = useState({});
  const [mode, setMode] = useState('products');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState({ key: 'offer_id', dir: 'asc' });

  // Выделение строк, инлайн-редактирование, master-поля над столбцами.
  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // { key, field }
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [master, setMaster] = useState({}); // { field: value }
  const [categoryOptions, setCategoryOptions] = useState({ gruppa: [], podgruppa: [] });
  const skipBlurRef = useRef(false); // не коммитить по blur после Escape/Enter

  // Импорт/экспорт Excel и отчёт.
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null); // { title, applied, errors[], skipped[], total }
  const fileInputRef = useRef(null);
  const versionRef = useRef(0);

  const getProductKey = (row) => `${row.organization || ''}|${row.barcode || row.offer_id || ''}`;

  const loadCategoryOptions = useCallback(async () => {
    try {
      const [groupsResp, subgroupsResp] = await Promise.all([
        axios.get('/filters/groups'),
        axios.get('/filters/subgroups'),
      ]);
      setCategoryOptions({
        gruppa: groupsResp.data.groups || [],
        podgruppa: subgroupsResp.data.subgroups || [],
      });
    } catch (_) {
      // Справочники улучшают ввод, но их сбой не должен блокировать редактирование.
    }
  }, []);

  const load = useCallback(async (overridePage) => {
    const version = ++versionRef.current;
    setLoading(true);
    try {
      const p = overridePage ?? page;
      const params = {
        ...buildFilterParams(filters),
        mode,
        limit: rowsPerPage,
        offset: p * rowsPerPage,
        sort_key: sort.key,
        sort_dir: sort.dir,
      };
      const resp = await axios.get('/products/list', { params });
      if (version === versionRef.current) {
        setRows(resp.data.rows || []);
        setTotal(resp.data.total || 0);
      }
    } catch (e) {
      if (version === versionRef.current) {
        showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
      }
    } finally {
      if (version === versionRef.current) setLoading(false);
    }
  }, [filters, mode, page, rowsPerPage, sort, showError]);

  useEffect(() => { load(); }, [page, rowsPerPage, mode, sort]);
  useEffect(() => { loadCategoryOptions(); }, [loadCategoryOptions]);

  const handleLoad = () => {
    setPage(0);
    load(0);
  };

  const handleModeChange = (_, newMode) => {
    if (newMode && newMode !== mode) {
      setMode(newMode);
      setPage(0);
      setSelected(new Set());
      setEditing(null);
      setSort({ key: newMode === 'groups' ? 'gruppa' : 'offer_id', dir: 'asc' });
    }
  };

  const handleSort = (key) => {
    setPage(0);
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const fmtMoney = (v) => {
    if (v == null || v === '') return '—';
    return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtInt = (v) => {
    if (v == null || v === '') return '—';
    return Number(v).toLocaleString('ru-RU');
  };

  const displayValue = (row, f) => {
    const v = row[f.field];
    if (v == null || v === '') return '—';
    if (f.type === 'money') return `${fmtMoney(v)} ₽`;
    if (f.type === 'percent') {
      return `${Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`;
    }
    if (f.type === 'int') return fmtInt(v);
    return v;
  };

  const numericStep = (type) => (type === 'money' || type === 'percent' ? '0.01' : 1);

  // ─── Выделение строк ─────────────────────────────────────────────────────
  const selectableRows = useMemo(
    () => rows.filter((r) => r.organization && r.barcode),
    [rows]
  );
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(getProductKey(r)));
  const someSelected = selectableRows.some((r) => selected.has(getProductKey(r)));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableRows.forEach((r) => next.delete(getProductKey(r)));
      } else {
        selectableRows.forEach((r) => next.add(getProductKey(r)));
      }
      return next;
    });
  };

  const toggleRow = (row) => {
    const key = getProductKey(row);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(getProductKey(r))),
    [rows, selected]
  );

  // ─── Сохранение изменений ────────────────────────────────────────────────
  const saveUpdates = async (items, { successMsg } = {}) => {
    if (!items.length) return;
    setSaving(true);
    try {
      const resp = await axios.post('/products/bulk-update', { items });
      const { applied = 0, errors = [], skipped = [] } = resp.data || {};
      if (errors.length || skipped.length) {
        setReport({ title: 'Результат обновления', applied, errors, skipped, total: items.length });
        showWarning(`Применено: ${applied}, ошибок: ${errors.length}, пропущено: ${skipped.length}`);
      } else {
        showSuccess(successMsg || `Обновлено товаров: ${applied}`);
      }
      setEditing(null);
      await Promise.all([load(), loadCategoryOptions()]);
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row, field) => {
    if (!row.organization || !row.barcode) {
      showWarning('Для редактирования нужны организация и баркод товара');
      return;
    }
    skipBlurRef.current = false;
    setEditing({ key: getProductKey(row), field });
    const v = row[field];
    setDraft(v == null ? '' : String(v));
  };

  const cancelEdit = () => { setEditing(null); setDraft(''); };

  const commitEdit = (row, field, value = draft) => {
    const val = String(value).trim();
    if (val === '') { cancelEdit(); return; }
    saveUpdates([{ organization: row.organization, barcode: row.barcode, [field]: val }], {
      successMsg: `${EDITABLE_MAP[field].label} сохранено`,
    });
  };

  const applyMaster = (field) => {
    const value = master[field];
    if (value === '' || value == null) { showWarning('Введите значение в поле над столбцом'); return; }
    const targets = selectedRows.filter((r) => r.organization && r.barcode);
    if (!targets.length) {
      showWarning('Выделите строки (чекбоксами) для применения');
      return;
    }
    const items = targets.map((r) => ({ organization: r.organization, barcode: r.barcode, [field]: value }));
    saveUpdates(items, { successMsg: `«${EDITABLE_MAP[field].label}» применено к ${items.length} товарам` });
  };

  const isCategoryField = (field) => field === 'gruppa' || field === 'podgruppa';

  // ─── Excel: выгрузка / загрузка ──────────────────────────────────────────
  const exportExcel = async () => {
    try {
      const resp = await axios.get('/products/export', {
        params: buildFilterParams(filters),
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(resp.data);
      a.download = `Товары_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || 'Не удалось выгрузить файл');
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const resp = await axios.post('/products/import', file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      const { applied = 0, errors = [], skipped = [], total_rows = 0 } = resp.data || {};
      setReport({ title: 'Результат импорта из Excel', applied, errors, skipped, total: total_rows });
      if (!errors.length && !skipped.length) {
        showSuccess(`Импорт завершён. Обновлено товаров: ${applied}`);
      } else {
        showWarning(`Импорт: применено ${applied}, ошибок ${errors.length}, пропущено ${skipped.length}`);
      }
      await load();
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  const cellSx = { py: 0.75, px: 1, fontSize: '0.85rem', whiteSpace: 'nowrap' };
  const headerSx = { ...cellSx, fontWeight: 'bold', bgcolor: 'grey.100' };
  const masterHeaderSx = { py: 0.25, px: 0.5, bgcolor: '#eef4fb', verticalAlign: 'middle', whiteSpace: 'nowrap' };

  const renderMpChip = (mpVal) => {
    const label = MP_MAP[mpVal] || mpVal;
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: MP_COLORS[mpVal] || 'grey.400',
          color: MP_TEXT_COLORS[mpVal] || '#fff',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 22,
        }}
      />
    );
  };

  // Колонки режима «по товарам». editable-колонки описываются EDITABLE_FIELDS.
  const productColumns = [
    { key: '__select', label: '', sortable: false, width: 44 },
    { key: 'photo', label: 'Фото', sortable: false, width: 56 },
    { key: 'offer_id', label: 'Артикул' },
    { key: 'content_name', label: 'Название' },
    { key: 'barcode', label: 'Баркод' },
    { key: 'product_id', label: 'ID' },
    { key: 'mp', label: 'МП', sortable: false, width: 100 },
    ...EDITABLE_FIELDS.map((f) => ({ key: f.field, label: f.label, editable: true, align: f.align, width: f.width })),
    { key: 'content_photo_count', label: 'Фото', align: 'right', width: 60 },
    { key: 'price', label: 'Цена', align: 'right' },
  ];

  const groupColumns = [
    { key: 'gruppa', label: 'Группа' },
    { key: 'podgruppa', label: 'Подгруппа' },
    { key: 'mp_list', label: 'МП', sortable: false, width: 140 },
    { key: 'cnt', label: 'Кол-во товаров', align: 'right' },
    { key: 'avg_price', label: 'Средняя цена', align: 'right' },
  ];

  const columns = mode === 'groups' ? groupColumns : productColumns;

  const renderEditableCell = (row, field) => {
    const f = EDITABLE_MAP[field];
    const key = getProductKey(row);
    const isEditing = editing && editing.key === key && editing.field === field;
    const disabled = !row.organization || !row.barcode;
    if (isEditing) {
      if (isCategoryField(field)) {
        return (
          <Autocomplete
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            openOnFocus
            options={categoryOptions[field]}
            value={draft}
            inputValue={draft}
            onInputChange={(_, value) => setDraft(value)}
            onChange={(_, value) => {
              if (value == null) return;
              skipBlurRef.current = true;
              commitEdit(row, field, value);
            }}
            filterOptions={filterCategoryOptions}
            renderOption={(props, option) => {
              const { key, ...liProps } = props;
              const isNew = isNewCategoryOption(categoryOptions[field], option);
              return (
                <li key={key} {...liProps}>
                  {isNew ? `Создать «${option}»` : option}
                </li>
              );
            }}
            noOptionsText="Введите название, чтобы создать"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                autoFocus
                placeholder="найти или создать"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    skipBlurRef.current = true;
                    cancelEdit();
                  }
                }}
                onBlur={() => {
                  if (skipBlurRef.current) { skipBlurRef.current = false; return; }
                  commitEdit(row, field);
                }}
              />
            )}
            sx={{ width: 180 }}
            disabled={saving}
          />
        );
      }
      return (
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); skipBlurRef.current = true; commitEdit(row, field); }
            else if (e.key === 'Escape') { e.preventDefault(); skipBlurRef.current = true; cancelEdit(); }
          }}
          onBlur={() => {
            if (skipBlurRef.current) { skipBlurRef.current = false; return; }
            commitEdit(row, field);
          }}
          size="small"
          type={f.type === 'text' ? 'text' : 'number'}
          inputProps={f.type === 'text' ? {} : { min: 0, step: numericStep(f.type), max: f.type === 'percent' ? 100 : undefined }}
          sx={{ width: f.type === 'text' ? 140 : 100 }}
          autoFocus
          disabled={saving}
        />
      );
    }
    return (
      <Tooltip title={disabled ? 'Нет баркода — редактирование недоступно' : 'Двойной клик, чтобы изменить'} arrow>
        <Box
          onDoubleClick={() => !disabled && startEdit(row, field)}
          sx={{
            cursor: disabled ? 'default' : 'text',
            minHeight: 20,
            borderRadius: 0.5,
            px: 0.5,
            '&:hover': disabled ? {} : { bgcolor: 'action.hover' },
          }}
        >
          {displayValue(row, f)}
        </Box>
      </Tooltip>
    );
  };

  const renderProductCell = (col, row) => {
    if (col.key === '__select') {
      const disabled = !row.organization || !row.barcode;
      return (
        <Checkbox
          size="small"
          checked={selected.has(getProductKey(row))}
          onChange={() => toggleRow(row)}
          disabled={disabled}
          sx={{ p: 0.25 }}
        />
      );
    }
    if (col.key === 'photo') {
      const imgSrc = pickProductImage(row);
      return imgSrc ? (
        <Avatar variant="rounded" src={imgSrc} sx={{ width: 40, height: 40 }} imgProps={{ loading: 'lazy' }}>—</Avatar>
      ) : (
        <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.200', color: 'grey.500', fontSize: 12 }}>—</Avatar>
      );
    }
    if (col.key === 'offer_id') {
      return (
        <Tooltip title={row.brand || ''} placement="top" arrow>
          <span>{row.offer_id || '—'}</span>
        </Tooltip>
      );
    }
    if (col.key === 'content_name') {
      return row.content_name ? (
        <Typography variant="body2" sx={{ fontSize: '0.82rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}>
          {row.content_name}
        </Typography>
      ) : '—';
    }
    if (col.key === 'mp') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(Array.isArray(row.mp_list) ? row.mp_list : []).map(mp => <span key={mp}>{renderMpChip(mp)}</span>)}
        </Box>
      );
    }
    if (col.editable) return renderEditableCell(row, col.key);
    if (col.key === 'content_photo_count') return row.content_photo_count != null ? row.content_photo_count : '—';
    if (col.key === 'price') return row.price ? `${fmtMoney(row.price)} ₽` : '—';
    return row[col.key] || '—';
  };

  return (
    <Box>
      <ProductsTabs />
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Товары МП
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <ProductFilters value={filters} onChange={setFilters} showExcludeMode={false} />
        </Box>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs="auto">
            <Button
              variant="contained" onClick={handleLoad} disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              Загрузить
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportExcel}
              disabled={loading || mode !== 'products'}
            >
              Скачать Excel
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Button
              variant="outlined"
              startIcon={importing ? <CircularProgress size={18} /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={importing || mode !== 'products'}
            >
              Загрузить Excel
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={onImportFile}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">Отображение:</Typography>
          <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
            <ToggleButton value="products">
              <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" />
              По товарам
            </ToggleButton>
            <ToggleButton value="groups">
              <CategoryIcon sx={{ mr: 0.5 }} fontSize="small" />
              По группам
            </ToggleButton>
          </ToggleButtonGroup>
          {total > 0 && (
            <Typography variant="body2" color="text.secondary">
              Найдено: {total.toLocaleString('ru-RU')}
            </Typography>
          )}
          {mode === 'products' && selected.size > 0 && (
            <Chip
              size="small"
              color="primary"
              label={`Выделено: ${selected.size}`}
              onDelete={() => setSelected(new Set())}
            />
          )}
        </Box>
        {mode === 'products' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Выделите строки чекбоксами и введите значение в поле над нужным столбцом, затем нажмите «применить к выделенным». Либо дважды кликните по ячейке, чтобы изменить одно значение. Группу и подгруппу можно выбрать из списка или ввести новое название — отдельного справочника нет, имя появится, как только сохранится хотя бы на одном товаре.
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.key}
                    sx={headerSx}
                    align={col.align || 'left'}
                    width={col.width}
                    padding={col.key === '__select' ? 'checkbox' : 'normal'}
                  >
                    {col.key === '__select' ? (
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onChange={toggleAll}
                        sx={{ p: 0.25 }}
                      />
                    ) : col.sortable === false ? col.label : (
                      <TableSortLabel
                        active={sort.key === col.key}
                        direction={sort.key === col.key ? sort.dir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              {/* Master-строка: поля массового изменения над редактируемыми столбцами */}
              {mode === 'products' && (
                <TableRow>
                  {columns.map(col => (
                    <TableCell key={`m-${col.key}`} sx={masterHeaderSx} align={col.align || 'left'}>
                      {col.editable && isCategoryField(col.key) ? (
                        <Autocomplete
                          freeSolo
                          selectOnFocus
                          clearOnBlur={false}
                          handleHomeEndKeys
                          openOnFocus
                          options={categoryOptions[col.key]}
                          value={master[col.key] ?? ''}
                          inputValue={master[col.key] ?? ''}
                          onInputChange={(_, value) => setMaster((m) => ({ ...m, [col.key]: value }))}
                          onChange={(_, value) => setMaster((m) => ({ ...m, [col.key]: value ?? '' }))}
                          filterOptions={filterCategoryOptions}
                          renderOption={(props, option) => {
                            const { key, ...liProps } = props;
                            const isNew = isNewCategoryOption(categoryOptions[col.key], option);
                            return (
                              <li key={key} {...liProps}>
                                {isNew ? `Создать «${option}»` : option}
                              </li>
                            );
                          }}
                          noOptionsText="Введите название, чтобы создать"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="найти или создать"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                    <Tooltip title="Применить к выделенным строкам" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          edge="end"
                                          onClick={() => applyMaster(col.key)}
                                          disabled={saving || selected.size === 0}
                                          sx={{ p: 0.25, mr: -0.5 }}
                                        >
                                          <PlaylistAddCheckIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{
                            width: 130,
                            bgcolor: 'background.paper',
                            '& .MuiInputBase-root': {
                              minHeight: 26,
                              py: '0 !important',
                            },
                            '& .MuiAutocomplete-input': {
                              minWidth: '0 !important',
                              p: '3px 4px !important',
                              fontSize: '0.78rem',
                            },
                            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                              p: 0.25,
                            },
                          }}
                          disabled={saving}
                        />
                      ) : col.editable ? (
                        <TextField
                          value={master[col.key] ?? ''}
                          onChange={(e) => setMaster((m) => ({ ...m, [col.key]: e.target.value }))}
                          size="small"
                          type={EDITABLE_MAP[col.key].type === 'text' ? 'text' : 'number'}
                          placeholder="всем"
                          inputProps={{
                            ...(EDITABLE_MAP[col.key].type === 'text' ? {} : { min: 0, step: numericStep(EDITABLE_MAP[col.key].type), max: EDITABLE_MAP[col.key].type === 'percent' ? 100 : undefined }),
                            style: { padding: '3px 4px', fontSize: '0.78rem' },
                          }}
                          sx={{ width: '100%', maxWidth: EDITABLE_MAP[col.key].type === 'text' ? 130 : 96, bgcolor: '#fff' }}
                          onKeyDown={(e) => { if (e.key === 'Enter') applyMaster(col.key); }}
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="Применить к выделенным строкам" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    edge="end"
                                    onClick={() => applyMaster(col.key)}
                                    disabled={saving || selected.size === 0}
                                    sx={{ p: 0.25, mr: -0.5 }}
                                  >
                                    <PlaylistAddCheckIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ),
                          }}
                        />
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {rows.length === 0 && !loading ? 'Нажмите "Загрузить" для получения данных' : 'Нет данных'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : mode === 'products' ? (
                rows.map((row, idx) => {
                  const isSel = selected.has(getProductKey(row));
                  return (
                    <TableRow
                      key={`${row.organization}-${row.barcode || row.offer_id}-${idx}`}
                      hover
                      selected={isSel}
                    >
                      {productColumns.map((col) => {
                        // Клик по интерактивным ячейкам не должен открывать карточку в новой вкладке.
                        const interactive = col.key === '__select' || col.editable;
                        const canOpen = !interactive && row.podgruppa;
                        return (
                          <TableCell
                            key={col.key}
                            sx={{ ...cellSx, ...(canOpen ? { cursor: 'pointer' } : {}), ...(col.key === 'content_name' ? { whiteSpace: 'normal', maxWidth: 300 } : {}), ...(col.key === 'photo' ? { py: 0.5 } : {}) }}
                            align={col.align || 'left'}
                            padding={col.key === '__select' ? 'checkbox' : 'normal'}
                            onClick={interactive ? (e) => e.stopPropagation() : () => canOpen && goToDetails(row, false)}
                          >
                            {renderProductCell(col, row)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                rows.map((row, idx) => (
                  <TableRow
                    key={`${row.gruppa}-${row.podgruppa}-${idx}`}
                    hover
                    onClick={() => goToDetails(row, true)}
                    sx={{ cursor: row.podgruppa ? 'pointer' : 'default' }}
                  >
                    <TableCell sx={cellSx}>{row.gruppa || '—'}</TableCell>
                    <TableCell sx={cellSx}>{row.podgruppa || '—'}</TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(Array.isArray(row.mp_list) ? row.mp_list : []).map(mp => <span key={mp}>{renderMpChip(mp)}</span>)}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">{row.cnt}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      {row.avg_price ? `${fmtMoney(row.avg_price)} ₽` : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 250]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} из ${count !== -1 ? count : `более ${to}`}`
          }
        />
      </Paper>

      {/* Отчёт об импорте / массовом обновлении */}
      <Dialog open={!!report} onClose={() => setReport(null)} maxWidth="md" fullWidth>
        <DialogTitle>{report?.title || 'Результат'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity={(report?.errors?.length || report?.skipped?.length) ? 'warning' : 'success'}>
              Обработано строк: {report?.total ?? 0}. Успешно применено: <b>{report?.applied ?? 0}</b>.
              {report?.errors?.length ? ` Ошибок: ${report.errors.length}.` : ''}
              {report?.skipped?.length ? ` Пропущено: ${report.skipped.length}.` : ''}
            </Alert>

            {report?.errors?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="error" gutterBottom>Ошибки заполнения</Typography>
                <ReportTable items={report.errors} />
              </Box>
            )}
            {report?.skipped?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Необработанные строки</Typography>
                <ReportTable items={report.skipped} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReport(null)} variant="contained">Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Таблица строк отчёта (ошибки / пропуски). Показывает до 300 строк.
function ReportTable({ items }) {
  const shown = items.slice(0, 300);
  return (
    <TableContainer sx={{ maxHeight: 260, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: 70 }}>Строка</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Организация</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Баркод</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Причина</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((it, i) => (
            <TableRow key={i}>
              <TableCell>{it.row ?? '—'}</TableCell>
              <TableCell>{it.organization || '—'}</TableCell>
              <TableCell>{it.barcode || '—'}</TableCell>
              <TableCell>{it.reason || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {items.length > shown.length && (
        <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
          Показаны первые {shown.length} из {items.length}.
        </Typography>
      )}
    </TableContainer>
  );
}
